import React, { useRef, useState } from 'react';
import { Aperture, Camera, StopCircle } from 'react-feather';
import { ScreenshotButton } from './buttons/DrawButton';
import Button from '../button';
import { useIsMobile } from '../hooks/useIsMobile';
import useLayersStore from '../stores/layersStore';

function LeftSection() {
  const featureEnabled = useLayersStore((state) => state.featureEnabled);
  const [isRecording, setIsRecording] = useState(false);
  const [showMobilePopup, setShowMobilePopup] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const isMobileDevice = useIsMobile();

  const handleScreenshot = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ preferCurrentTab: true });
      const video = document.createElement("video");
      video.srcObject = stream;
      await video.play();
  
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0);
      
      // Stop all tracks
      stream.getTracks().forEach(track => track.stop());
      
      // Save the image
      const dataURL = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.download = "screenshot.png";
      link.href = dataURL;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to capture screenshot:", error);
    }
  };

  const handleRecording = async () => {
    console.log("Recording button clicked, isMobileDevice:", isMobileDevice);
    if (isMobileDevice) {
      setShowMobilePopup(true);
      return;
    }

    if (!isRecording) {
      // Start recording
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({ preferCurrentTab: true });

        // Add track ended event listener
        stream.getTracks().forEach(track => {
          track.onended = () => {
            mediaRecorderRef.current?.stop();
            setIsRecording(false);
          };
        });

        const mimeType = [
          'video/mp4;codecs="avc1.42E01E"',
          'video/mp4',
          'video/webm;codecs=h264',
          'video/webm;codecs=vp9',
          'video/webm;codecs=vp8',
          'video/webm',
        ].find(t => MediaRecorder.isTypeSupported(t)) || 'video/webm';
        const ext = mimeType.startsWith('video/mp4') ? 'mp4' : 'webm';
        const mediaRecorder = new MediaRecorder(stream, { mimeType });
        mediaRecorderRef.current = mediaRecorder;
        chunksRef.current = [];

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            chunksRef.current.push(event.data);
          }
        };

        mediaRecorder.onstop = () => {
          const blob = new Blob(chunksRef.current, { type: mediaRecorder.mimeType });
          const url = URL.createObjectURL(blob);
          
          // Create download link
          const a = document.createElement('a');
          a.href = url;
          a.download = `screen-recording-${new Date().toISOString()}.${ext}`;
          a.click();

          // Cleanup
          stream.getTracks().forEach(track => track.stop());
          URL.revokeObjectURL(url);
        };

        mediaRecorder.start();
        setIsRecording(true);
      } catch (err) {
        console.error("Error starting recording:", err);
      }
    } else {
      // Stop recording
      mediaRecorderRef.current?.stop();
      setIsRecording(false);
    }
  };

  return (
    <>
      <div id="LeftSection" className='absolute left-0 top-0 flex flex-col w-min justify-end px-4 h-full py-16 gap-2 pointer-events-none'>
        <ScreenshotButton />
        {featureEnabled.screenRecord && (
          <Button className='flex gap-1 w-min pointer-events-auto' onClick={handleRecording}>
          {isRecording ? (
            <StopCircle size={20} className="blink-red" />
          ) : (
            <Aperture size={20} />
          )}
          <div className='hidden sm:block'>{isRecording ? 'Stop' : 'Record'}</div>
          <div className="sm:hidden">{isRecording ? 'Stop' : 'Rec.'}</div>
        </Button>
        )}
      </div>
      
      {showMobilePopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full text-center">
            <p className="text-gray-800 mb-4">
              For security reasons this feature needs to be manually enabled.{' '}
              <a 
                href={`https://biocloud3d.com/learn-how-php?tab=record-video`}
                className="text-blue-600 underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn How
              </a>
            </p>
            <button 
              onClick={() => setShowMobilePopup(false)}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default LeftSection; 